/**
 * 그룹(학원) 관리 API 서비스
 * - 그룹 CRUD 작업
 * - 그룹 통계 조회
 */

import axios from '../config/axios';
import type {
  Group,
  GroupDetail,
  GroupStatistics,
  GroupCreateRequest,
  GroupUpdateRequest,
  GroupListParams,
  GroupListResponse,
  GroupDeleteResponse,
} from '../types/group';

/**
 * 그룹 서비스 클래스
 */
class GroupService {
  /**
   * 그룹 목록 조회
   *
   * @param params - 조회 파라미터 (페이지네이션, 검색, 필터)
   * @returns 그룹 목록 및 페이지 정보
   *
   * @example
   * const result = await groupService.getGroups({ page: 1, page_size: 20 });
   */
  async getGroups(params: GroupListParams = {}): Promise<GroupListResponse> {
    const response = await axios.get<GroupListResponse>('/groups', {
      params,
    });
    return response.data;
  }

  /**
   * 그룹 상세 조회 (통계 포함)
   *
   * @param groupId - 그룹 ID (UUID)
   * @returns 그룹 상세 정보 + 통계
   *
   * @example
   * const group = await groupService.getGroupById('uuid-here');
   */
  async getGroupById(groupId: string): Promise<GroupDetail> {
    const response = await axios.get<GroupDetail>(`/groups/${groupId}`);
    return response.data;
  }

  /**
   * 그룹 생성 (admin만)
   *
   * @param data - 그룹 생성 요청 데이터
   * @returns 생성된 그룹 정보
   *
   * @example
   * const newGroup = await groupService.createGroup({
   *   name: '강남학원',
   *   code: 'gangnam123',
   *   description: '강남구 소재 수학학원',
   * });
   *
   * @throws {Error} 중복된 그룹명/코드, 권한 없음 등
   */
  async createGroup(data: GroupCreateRequest): Promise<Group> {
    const response = await axios.post<Group>('/groups', data);
    return response.data;
  }

  /**
   * 그룹 정보 수정
   *
   * @param groupId - 그룹 ID (UUID)
   * @param data - 수정할 데이터
   * @returns 수정된 그룹 정보
   *
   * @example
   * const updated = await groupService.updateGroup('uuid', {
   *   name: '강남학원 (수정)',
   *   description: '새로운 설명',
   * });
   *
   * @note
   * - admin: 모든 필드 수정 가능
   * - group_admin: name, description만 수정 가능 (자기 그룹만)
   * - code는 수정 불가 (불변)
   */
  async updateGroup(groupId: string, data: GroupUpdateRequest): Promise<Group> {
    const response = await axios.put<Group>(`/groups/${groupId}`, data);
    return response.data;
  }

  /**
   * 그룹 삭제 (admin만, Soft Delete)
   *
   * @param groupId - 그룹 ID (UUID)
   * @returns 삭제 결과 메시지
   *
   * @example
   * await groupService.deleteGroup('uuid');
   *
   * @throws {Error} 소속 사용자가 있는 경우 삭제 불가
   */
  async deleteGroup(groupId: string): Promise<GroupDeleteResponse> {
    const response = await axios.delete<GroupDeleteResponse>(`/groups/${groupId}`);
    return response.data;
  }

  /**
   * 그룹 통계 조회
   *
   * @param groupId - 그룹 ID (UUID)
   * @returns 그룹 상세 통계 정보
   *
   * @example
   * const stats = await groupService.getGroupStatistics('uuid');
   * console.log(`전체 사용자: ${stats.total_users}명`);
   * console.log(`정답률: ${stats.overall_accuracy}%`);
   */
  async getGroupStatistics(groupId: string): Promise<GroupStatistics> {
    const response = await axios.get<GroupStatistics>(`/groups/${groupId}/statistics`);
    return response.data;
  }
}

/**
 * 그룹 서비스 싱글톤 인스턴스
 *
 * @example
 * import { groupService } from '@/services/groupService';
 *
 * // 그룹 목록 조회
 * const groups = await groupService.getGroups({ page: 1 });
 *
 * // 그룹 생성
 * const newGroup = await groupService.createGroup({ name: '...' });
 */
export const groupService = new GroupService();
